import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'

type Overlay = Record<string, unknown>
export type Practice = {
  /** Unsaved edits to one document, by dotted path. */
  overlay: (docPath: string) => Overlay
  update: (docPath: string, fn: (o: Overlay) => Overlay) => void
}

const EMPTY: Overlay = {}
const Ctx = createContext<Practice | null>(null)

/**
 * Practice mode for staff previewing as the client: edits, confirmations and undos behave exactly as they do for
 * the client, across every step, but live only in memory. Nothing reaches Firestore, and it all goes when the
 * preview closes.
 */
export function PracticeProvider({ active, children }: { active: boolean; children: ReactNode }) {
  const [store, setStore] = useState<Record<string, Overlay>>({})
  const update = useCallback((docPath: string, fn: (o: Overlay) => Overlay) => {
    setStore((s) => {
      const cur = s[docPath] ?? EMPTY
      const next = fn(cur)
      return next === cur ? s : { ...s, [docPath]: next }
    })
  }, [])
  const value = useMemo<Practice | null>(() => (active ? { overlay: (p) => store[p] ?? EMPTY, update } : null), [active, store, update])
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

/** The practice store while previewing as the client; null everywhere else, where edits save for real. */
export function usePractice(): Practice | null {
  return useContext(Ctx)
}

/** A document with unsaved dotted-path edits laid over it; `undefined` removes the key. */
export function applyOverlay<T>(data: T, overlay: Overlay): T {
  if (Object.keys(overlay).length === 0) return data
  const clone = JSON.parse(JSON.stringify(data)) as Record<string, unknown>
  for (const [k, v] of Object.entries(overlay)) {
    const parts = k.split('.')
    let cur = clone
    for (const part of parts.slice(0, -1)) {
      if (typeof cur[part] !== 'object' || cur[part] === null) cur[part] = {}
      cur = cur[part] as Record<string, unknown>
    }
    if (v === undefined) delete cur[parts[parts.length - 1]!]
    else cur[parts[parts.length - 1]!] = v
  }
  return clone as T
}
